export default ({
  ui: {
    colors: {
      primary: 'monster', // brand red
      secondary: 'monster-blue', // brand blue
      gold: 'monster-gold', // award gold (extended)
      neutral: 'stone', // warm grey to sit cohesively under red + gold
    },

    button: {
      slots: {
        // NOTE: hover/press translate + shadow swap is handled by
        // `<MMotionButton>` (motion-v gestures). This base only carries
        // typography + min-width baseline + the shadow-on-hover swap.
        // `min-w-[125px]` ensures short-text buttons ("OK", "Go") never
        // look stamp-tiny — matches v1's `.m-button` rule.
        base: [
          'font-display tracking-wide uppercase font-bold',
          'transition-shadow duration-200',
          'inline-flex items-center justify-center gap-2',
          'min-h-11 min-w-[125px]',
        ],
      },
      variants: {
        // Brand-only variants. `fancy` paints the rainbow gradient that
        // pulses on hover. `textured` overlays the noise/items texture.
        // Both depend on classes defined in main.css so the CSS lives
        // alongside the rest of the brand utilities.
        variant: {
          fancy: {
            base: 'btn-fancy btn-textured bg-primary !text-white shadow-monster hover:shadow-monster-lg active:shadow-monster-sm',
          },
          textured: {
            base: 'btn-textured shadow-monster hover:shadow-monster-lg active:shadow-monster-sm',
          },
        },
        // `lg`+ get a chunkier min-width so heroes have presence
        // (matches v1's `.m-button--large` rule). Smaller sizes inherit
        // the base 125px floor.
        size: {
          'lg': { base: 'min-w-[220px]' },
          'xl': { base: 'min-w-[260px]' },
          '2xl': {
            base: 'min-w-[300px] px-4 py-2.5 text-lg gap-2.5',
            leadingIcon: 'size-7',
            leadingAvatarSize: 'sm',
            trailingIcon: 'size-7',
          },
          '3xl': {
            base: 'min-w-[340px] px-5 py-3 text-xl gap-3',
            leadingIcon: 'size-8',
            leadingAvatarSize: 'md',
            trailingIcon: 'size-8',
          },
          '4xl': {
            base: 'min-w-[380px] px-6 py-4 text-2xl gap-3.5',
            leadingIcon: 'size-9',
            leadingAvatarSize: 'lg',
            trailingIcon: 'size-9',
          },
        },
        // `long` = the brand's "give the CTA real estate" pattern.
        // Fixed 400px, capped at viewport. Replaces v1's
        // `.m-button--long`. Caller doesn't need to write width classes.
        long: {
          true: {
            base: 'w-[400px] max-w-full',
          },
        },
      },
      defaultVariants: {
        color: 'primary',
        variant: 'solid',
        size: 'md',
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: 'solid',
          class: '!text-white shadow-monster hover:shadow-monster-lg active:shadow-monster-sm',
        },
        {
          color: 'secondary',
          variant: 'solid',
          class: '!text-white shadow-monster hover:shadow-monster-lg active:shadow-monster-sm',
        },
        {
          color: 'gold',
          variant: 'solid',
          class: 'text-monster-950 shadow-monster hover:shadow-monster-lg',
        },
        {
          color: 'primary',
          variant: 'textured',
          class: '!text-white bg-primary hover:bg-primary/75 active:bg-primary/75 disabled:bg-primary aria-disabled:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        },
        {
          color: 'secondary',
          variant: 'textured',
          class: '!text-white bg-secondary hover:bg-secondary/75 active:bg-secondary/75 disabled:bg-secondary aria-disabled:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary',
        },
        {
          color: 'gold',
          variant: 'textured',
          class: 'text-monster-950 bg-gold hover:bg-gold/75 active:bg-gold/75 disabled:bg-gold aria-disabled:bg-gold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold',
        },
        {
          color: 'neutral',
          variant: 'textured',
          class: '!text-white bg-neutral-400 hover:bg-neutral-600 active:bg-neutral-600 disabled:bg-neutral-700 aria-disabled:bg-neutral-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-700',
        },
        {
          color: 'white',
          variant: 'textured',
          class: 'bg-white',
        },
      ],
    },

    card: {
      slots: {
        root: 'rounded-2xl shadow-monster bg-grain ring-0',
        header: 'p-6',
        body: 'p-6',
        footer: 'p-6',
      },
      variants: {
        variant: {
          solid: { root: 'bg-default text-default' },
          outline: { root: 'bg-default text-default border border-default shadow-none' },
          soft: { root: 'bg-muted text-default shadow-monster-sm' },
          subtle: { root: 'bg-elevated text-default shadow-monster-sm' },
        },
      },
      defaultVariants: {
        variant: 'solid',
      },
    },

    badge: {
      slots: {
        base: 'rounded-full px-3 py-1 text-xs font-display font-bold tracking-wide uppercase',
      },
    },

    input: {
      slots: {
        base: 'min-h-11 rounded-lg shadow-monster-sm border-default focus:border-primary text-base',
      },
    },

    formField: {
      slots: {
        label: 'font-display font-bold tracking-wide',
      },
    },

    select: {
      slots: {
        base: 'min-h-11 rounded-lg shadow-monster-sm text-base',
      },
    },

    textarea: {
      slots: {
        base: 'min-h-11 rounded-lg shadow-monster-sm border-default focus:border-primary text-base',
      },
    },

    alert: {
      slots: {
        root: 'rounded-xl shadow-monster-sm',
      },
    },

    modal: {
      slots: {
        content: 'rounded-2xl shadow-monster-lg',
      },
    },

    container: {
      base: 'max-w-6xl mx-auto px-4 sm:px-6 lg:px-8',
    },
  },
})
